import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { countryDetails } from '../constants/listOfCountriesAndStates';
import { Autocomplete } from '@mui/material';
import {
    WorkflowCardBody,
    WorkflowCard ,
    WorkflowCardActions,
    useRegistrationWorkflowContext,WorkflowCardHeader, WorkflowCardInstructions

} from '@brightlayer-ui/react-auth-workflow';


export const TextFieldStyles = (theme) => ({
    mt: 4,
    [theme.breakpoints.down('sm')]: {
        mt: 3,
    },
});

export const CustomDetailsScreen = (props) => {

    const regWorkflow = useRegistrationWorkflowContext();
    const {  screenData, currentScreen, totalScreens } = regWorkflow;

    const { WorkflowCardHeaderProps, translations } = props;

    const [firstName, setFirstName] = useState(screenData?.Other?.customScreenOne?.firstName);
    const [lastName, setLastName] = useState(screenData?.Other?.customScreenOne?.lastName);
    const [country, setCountry] = useState(screenData?.Other?.customScreenOne?.country);
    const [phone, setPhone] = useState(screenData?.Other?.customScreenOne?.phone);
    const [region, setRegion] = useState(screenData?.Other?.customScreenOne?.region);
    const [allValid, setAllValid] = useState(false);

    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const phoneRef = useRef(null);
    const theme = useTheme();

    const workflowCardActionsProps = {
        showNext: true,
        nextLabel: translations.next,
        canGoNext: true,
        showPrevious: true,
        previousLabel: translations.back,
        canGoPrevious: true,
        currentStep: currentScreen,
        totalSteps: totalScreens,
        onNext: () => {
            regWorkflow.nextScreen({
                screenId: 'customScreenOne',
                values: { firstName, lastName, country, phone, region },
            });
        },
        onPrevious: () => {
            regWorkflow.previousScreen({
                screenId: 'customScreenOne',
                values: { firstName, lastName, country, phone, region },
            });
        },
    };

    const onDetailsChanged = (details) => {
        const { country, region } = details;
        const isValid = country?.length > 0 && region?.length > 0 && firstName?.length > 0 && lastName?.length > 0;
        setAllValid(isValid);
    };

    useEffect(() => {
        onDetailsChanged({ country, phone, region });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, phone, region]); // Do NOT include onDetailsChanged in the dependencies array here or you will run into an infinite loop of updates

    return (
        <>
            <WorkflowCard>
                <WorkflowCardHeader {...WorkflowCardHeaderProps} />
                <WorkflowCardInstructions instructions={translations.instructions} />
                <WorkflowCardBody>
                    <TextField
                        fullWidth
                        variant={'filled'}
                        inputRef={firstNameRef}
                        label={translations.firstNameLabel}
                        value={firstName}
                        placeholder={translations.firstNameLabel}
                        onChange={(evt) => setFirstName(evt.target.value)}
                        sx={TextFieldStyles(theme)}
                        inputProps={{
                            maxLength: 256,
                            'data-testid': 'custom-details-screen-firstName-field',
                        }}
                    />

                    <TextField
                        fullWidth
                        variant={'filled'}
                        inputRef={lastNameRef}
                        label={translations.lastNameLabel}
                        value={lastName}
                        placeholder={translations.lastNameLabel}
                        onChange={(evt) => setLastName(evt.target.value)}
                        sx={TextFieldStyles(theme)}
                        inputProps={{
                            maxLength: 256,
                            'data-testid': 'custom-details-screen-lastName-field',
                        }}
                    />

                    <TextField
                        fullWidth
                        variant={'filled'}
                        required={false}
                        inputRef={phoneRef}
                        label={translations.phoneLabel}
                        value={phone}
                        placeholder={translations.phonePlaceholder}
                        onChange={(evt) => setPhone(evt.target.value)}
                        sx={TextFieldStyles(theme)}
                        inputProps={{
                            'data-testid': 'custom-details-screen-phone-field',
                            maxLength: 256,
                        }}
                    />

                    <Autocomplete
                        data-testid="custom-details-screen-country-field"
                        options={countryDetails.map((country) => country.Name)}
                        onChange={(event, value) => setCountry(value)}
                        value={country}

                        renderInput={(params) => <TextField {...params} label={translations.countryLabel} />}
                        sx={TextFieldStyles(theme)}
                        onInputChange={(event, value, reason) => {
                            if (reason === 'clear') {
                                setRegion('');
                            }
                        }}
                        fullWidth
                    />
                    <Autocomplete
                        fullWidth
                        sx={TextFieldStyles(theme)}
                        data-testid="custom-details-screen-region-field"
                        disabled={!!!country}
                        defaultValue=""
                        options={
                            !!country
                                ? countryDetails
                                      .find(
                                          (countryOption) => countryOption.Name !== '' && countryOption.Name === country
                                      )
                                      .States.map((state) => state.Name)
                                : []
                        }
                        onChange={(event, value) => setRegion(value)}
                        value={region}

                        renderInput={(params) => <TextField {...params} label={translations.regionLabel} />}
                    />
                </WorkflowCardBody>
                <WorkflowCardActions {...workflowCardActionsProps} canGoNext={allValid} />
            </WorkflowCard>
        </>
    );
};

export const CustomDetailsScreenTwo = (props) => {
    const { WorkflowCardHeaderProps, translations } = props;
    const regWorkflow = useRegistrationWorkflowContext();
    const { screenData, currentScreen, totalScreens } = regWorkflow;

    const [company, setCompany] = useState(screenData?.Other?.customScreenTwo?.company);
    const [role, setRole] = useState(screenData?.Other?.customScreenTwo?.role);
    const [utility, setUtility] = useState(screenData?.Other?.customScreenTwo?.utility);
    const [allValid, setAllValid] = useState(false);
    const theme = useTheme();

    const companyRef = useRef(null);
    const roleRef = useRef(null);
    const utilityRef = useRef(null);

   

    const onDetailsChanged = (details) => {
        const { company, role } = details;

        // Check if all inputs have length > 0
        const isValid = company?.length > 0 && role?.length > 0 ;

        // Return the validation result
        setAllValid(isValid);
    };

    useEffect(() => {
        onDetailsChanged({ company, role, utility });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role, company, utility]); // Do NOT include onDetailsChanged in the dependencies array here or you will run into an infinite loop of updates

    const workflowCardActionsProps = {
        showNext: true,
        nextLabel: translations.next,
        canGoNext: true,
        showPrevious: true,
        previousLabel: translations.back,
        canGoPrevious: true,
        currentStep: currentScreen,
        totalSteps: totalScreens,
        onNext: () => {
            regWorkflow.nextScreen({
                screenId: 'customScreenTwo',
                values: { company, role, utility },
            });
        },
        onPrevious: () => {
            regWorkflow.previousScreen({
                screenId: 'customScreenTwo',
                values: { company, role, utility },
            });
        },
    };

    return (
        <>
            <WorkflowCard>
            <WorkflowCardHeader {...WorkflowCardHeaderProps} />
            <WorkflowCardInstructions instructions={translations.instruction} />
                <WorkflowCardBody>
                    <TextField
                        fullWidth
                        variant={'filled'}
                        inputRef={companyRef}
                        label={translations.orgLabel}
                        value={company}
                        placeholder={translations.orgPlaceholder}
                        onChange={(evt) => setCompany(evt.target.value)}
                       
                        inputProps={{
                            maxLength: 256,
                            'data-testid': 'custom-details-screen-company-field',
                        }}
                    />
                    <TextField
                        fullWidth
                        variant={'filled'}
                        inputRef={utilityRef}
                        label={translations.electricUtilityLabel}
                        value={utility}
                        placeholder={translations.electricUtilityPlaceholder}
                        sx={TextFieldStyles(theme)}
                        onChange={(evt) => setUtility(evt.target.value)}
                       
                        inputProps={{
                            maxLength: 256,
                            'data-testid': 'custom-details-screen-electric-utility-field',
                        }}
                    />
                    <TextField
                        fullWidth
                        variant={'filled'}
                        inputRef={roleRef}
                        label={translations.jobTitle}
                        value={role}
                        placeholder={translations.jobTitlePlaceholder}
                        sx={TextFieldStyles(theme)}
                        onChange={(evt) => setRole(evt.target.value)}
                       
                        inputProps={{
                            maxLength: 256,
                            'data-testid': 'custom-details-screen-job-title-field',
                        }}
                    />
                </WorkflowCardBody>
                <WorkflowCardActions {...workflowCardActionsProps} canGoNext={allValid} />
            </WorkflowCard>
        </>
    );
};
